/**
 * Auto-Match Referrals Component
 * Automatically matches referrals to nearest providers based on location
 * Replaces the old MatchingEngine component with real provider matching
 */

import { useState, useEffect } from "react";
import { ArrowLeft, MapPin, Send, RefreshCw, CheckCircle, Clock, UserCheck, Settings, ChevronDown, ChevronUp, Edit2, X } from "lucide-react";
import { config } from "@/src/config";
import { ProviderSelectionModal } from "./ProviderSelectionModal";
import { useFormattedDate } from '../hooks/useUserTimezone';
import { AutoMatchConfirmationDialog } from "./AutoMatchConfirmationDialog";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { EmptyState } from "@/components/ui/EmptyState";

// Use Trust Marketplace API for referrals, auto-match endpoints, and providers
const API_BASE_URL = config.api.baseUrl;

// Helper function to parse services which may be a string or array
const parseServices = (services: string | string[] | undefined | null): string[] => {
  if (!services) return [];
  if (Array.isArray(services)) return services;
  if (typeof services === 'string') {
    // Try to parse as JSON array
    try {
      const parsed = JSON.parse(services);
      if (Array.isArray(parsed)) return parsed;
      return [services];
    } catch {
      // If not JSON, treat as single service or comma-separated
      if (services.includes(',')) {
        return services.split(',').map(s => s.trim()).filter(Boolean);
      }
      return [services];
    }
  }
  return [];
};

interface BackNavigationProps {
  onBack?: () => void;
}

interface MatchedProvider {
  provider_id: string;
  provider_name: string;
  organization_name: string;
  distance_miles: number;
  services: string[];
  status: 'pending' | 'sent' | 'accepted' | 'not_selected';
  sent_at?: string;
  responded_at?: string;
}

interface AutoMatchedReferral {
  id: string;
  referral_number: string;
  first_name: string;
  last_name: string;
  status: string;
  primary_diagnosis: string;
  urgency_level: string;
  sdoh_needs: string[];
  zip_code: string;
  latitude?: number;
  longitude?: number;
  matched_providers?: MatchedProvider[];
  auto_matched_at?: string;
  accepted_provider_id?: string;
}

export function AutoMatchReferrals({ onBack }: BackNavigationProps) {
  const { fmt } = useFormattedDate();
  const [referrals, setReferrals] = useState<AutoMatchedReferral[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedReferralId, setExpandedReferralId] = useState<string | null>(null);
  const [processingReferralId, setProcessingReferralId] = useState<string | null>(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [editingReferral, setEditingReferral] = useState<AutoMatchedReferral | null>(null);
  const [providerToReplace, setProviderToReplace] = useState<string | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmingReferral, setConfirmingReferral] = useState<AutoMatchedReferral | null>(null);
  const [algorithmStatus, setAlgorithmStatus] = useState({
    status: 'Active',
    lastRun: new Date().toISOString(),
    totalMatched: 0,
    pendingMatches: 0,
  });

  // Fetch referrals and providers on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch referrals from Trust Marketplace API
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const referralsResponse = await fetch(`${API_BASE_URL}/api/referrals`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
        },
      });
      if (!referralsResponse.ok) throw new Error('Failed to fetch referrals');
      const referralsData = await referralsResponse.json();

      // Filter for pending and auto-matched referrals with use_matching_engine = true/1
      const autoMatchReferrals = referralsData.filter((r: any) => {
        const statusLower = r.status?.toLowerCase();
        const useMatchingEngine = r.use_matching_engine === true || r.use_matching_engine === 1;
        return useMatchingEngine && (statusLower === 'pending' || statusLower === 'auto-matched');
      });

      setReferrals(autoMatchReferrals);

      // Fetch providers from provider directory (endpoint requires auth + org scope)
      const providersResponse = await fetch(`${API_BASE_URL}/api/providers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
        },
      });
      if (providersResponse.ok) {
        const providersData = await providersResponse.json();
        // Handle both array response and object with providers array
        const providersList = Array.isArray(providersData) ? providersData : (providersData.providers || []);
        setProviders(providersList);
      }

      // Update algorithm status
      const autoMatched = autoMatchReferrals.filter((r: any) => r.status?.toLowerCase() === 'auto-matched').length;
      const pending = autoMatchReferrals.filter((r: any) => r.status?.toLowerCase() === 'pending').length;

      setAlgorithmStatus({
        status: 'Active',
        lastRun: new Date().toISOString(),
        totalMatched: autoMatched,
        pendingMatches: pending,
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return Math.round(distance * 10) / 10; // Round to 1 decimal
  };

  const toRad = (degrees: number): number => {
    return degrees * (Math.PI / 180);
  };

  const handleAutoMatch = async (referral: AutoMatchedReferral) => {
    setProcessingReferralId(referral.id);

    try {
      // Check if referral has geocoded location
      if (!referral.latitude || !referral.longitude) {
        alert('Referral missing geocoded location. Please ensure the referral has a valid address.');
        setProcessingReferralId(null);
        return;
      }

      // Fetch providers near this referral's location from provider-directory + saved_providers
      console.log(`🔍 Fetching providers within 50 miles of (${referral.latitude}, ${referral.longitude})`);

      const providersResponse = await fetch(
        `${API_BASE_URL}/api/providers?latitude=${referral.latitude}&longitude=${referral.longitude}&radius_miles=50`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          },
        }
      );

      if (!providersResponse.ok) {
        throw new Error('Failed to fetch providers from directory');
      }

      const nearbyProvidersData = await providersResponse.json();
      // Handle both array response and object with providers array
      const nearbyProviders = Array.isArray(nearbyProvidersData) ? nearbyProvidersData : (nearbyProvidersData.providers || []);
      console.log(`✅ Found ${nearbyProviders.length} providers nearby`);

      // Filter providers that have geocoding
      const geocodedProviders = nearbyProviders.filter((p: any) => p.latitude && p.longitude);

      if (geocodedProviders.length === 0) {
        alert('No geocoded providers found near this member location (50 mile radius)');
        setProcessingReferralId(null);
        return;
      }

      // Build matched providers list with distances
      const providersWithDistance = geocodedProviders.map((p: any) => {
        let distance = p.distance_miles;

        // If distance not provided (manual provider), calculate it
        if (!distance && p.latitude && p.longitude) {
          distance = calculateDistance(
            referral.latitude!,
            referral.longitude!,
            p.latitude,
            p.longitude
          );
        }

        return {
          provider_id: p.id || p.provider_id,
          provider_name: p.organization_name,
          organization_name: p.organization_name,
          distance_miles: distance || 999,
          services: p.services || [],
          status: 'pending' as const,
        };
      });

      // Sort by distance and take top 4
      providersWithDistance.sort((a, b) => a.distance_miles - b.distance_miles);
      const matchedProviders = providersWithDistance.slice(0, 4);

      console.log(`📍 Top 4 nearest providers:`, matchedProviders.map(p =>
        `${p.organization_name} (${p.distance_miles.toFixed(1)} mi)`
      ));

      // Call backend auto-match endpoint on Trust Marketplace API
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(`${API_BASE_URL}/api/referrals/${referral.id}/auto-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
        },
        body: JSON.stringify({
          matched_providers: matchedProviders,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || 'Failed to auto-match referral');
      }

      console.log(`✅ Successfully auto-matched referral ${referral.id}`);

      // Refresh data
      await fetchData();
      setExpandedReferralId(referral.id);

    } catch (err) {
      console.error('Error auto-matching referral:', err);
      alert(err instanceof Error ? err.message : 'Failed to auto-match referral');
    } finally {
      setProcessingReferralId(null);
    }
  };

  const handleSendToProviders = async (referral: AutoMatchedReferral) => {
    if (!referral.matched_providers || referral.matched_providers.length === 0) {
      alert('No matched providers to send to');
      return;
    }

    // Show confirmation dialog with communication methods
    setConfirmingReferral(referral);
    setShowConfirmationDialog(true);
  };

  const handleConfirmSend = async (selectedMethods: Map<string, any>) => {
    if (!confirmingReferral) return;

    setProcessingReferralId(confirmingReferral.id);

    try {
      // Mark providers as "sent" with their selected communication methods
      const updatedProviders = confirmingReferral.matched_providers!.map(p => {
        const method = selectedMethods.get(p.provider_id);
        return {
          ...p,
          status: 'sent' as const,
          sent_at: new Date().toISOString(),
          communication_method: method?.method_type,
          contact_value: method?.contact_value,
        };
      });

      // Update backend on Trust Marketplace API
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(`${API_BASE_URL}/api/referrals/${confirmingReferral.id}/auto-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
        },
        body: JSON.stringify({
          matched_providers: updatedProviders,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || 'Failed to send to providers');
      }

      // Refresh data
      await fetchData();

      alert(`Referral sent to ${updatedProviders.length} providers via their preferred communication methods`);

      // Close dialog
      setShowConfirmationDialog(false);
      setConfirmingReferral(null);

    } catch (err) {
      console.error('Error sending to providers:', err);
      alert('Failed to send to providers');
    } finally {
      setProcessingReferralId(null);
    }
  };

  const toggleReferralExpansion = (referralId: string) => {
    setExpandedReferralId(expandedReferralId === referralId ? null : referralId);
  };

  const handleChangeProvider = (referral: AutoMatchedReferral, providerIdToReplace: string) => {
    setEditingReferral(referral);
    setProviderToReplace(providerIdToReplace);
    setShowProviderModal(true);
  };

  const handleProviderSelected = async (newProvider: any) => {
    if (!editingReferral || !providerToReplace) return;

    setProcessingReferralId(editingReferral.id);

    try {
      // Calculate distance for new provider
      const distance = editingReferral.latitude && editingReferral.longitude && newProvider.latitude && newProvider.longitude
        ? calculateDistance(editingReferral.latitude, editingReferral.longitude, newProvider.latitude, newProvider.longitude)
        : 0;

      // Update matched providers - replace the old provider with new one
      const updatedProviders = editingReferral.matched_providers?.map(p => {
        if (p.provider_id === providerToReplace) {
          return {
            provider_id: newProvider.id,
            provider_name: newProvider.organization_name,
            organization_name: newProvider.organization_name,
            distance_miles: distance,
            services: newProvider.services || [],
            status: 'pending' as const,
          };
        }
        return p;
      }) || [];

      // Update backend on Trust Marketplace API
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(`${API_BASE_URL}/api/referrals/${editingReferral.id}/auto-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
        },
        body: JSON.stringify({
          matched_providers: updatedProviders,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || 'Failed to update provider');
      }

      // Refresh data
      await fetchData();
      setShowProviderModal(false);
      setEditingReferral(null);
      setProviderToReplace(null);

    } catch (err) {
      console.error('Error updating provider:', err);
      alert('Failed to update provider');
    } finally {
      setProcessingReferralId(null);
    }
  };

  const handleRemoveProvider = async (referral: AutoMatchedReferral, providerIdToRemove: string) => {
    const isLastProvider = referral.matched_providers && referral.matched_providers.length === 1;

    const confirmMessage = isLastProvider
      ? 'This is the last provider. Removing it will cancel the auto-match and revert the referral to Pending status. Continue?'
      : 'Are you sure you want to remove this provider from the match?';

    if (!confirm(confirmMessage)) {
      return;
    }

    setProcessingReferralId(referral.id);

    try {
      if (isLastProvider) {
        // Removing last provider - revert to Pending status on Trust Marketplace API
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');

        const response = await fetch(`${API_BASE_URL}/api/referrals/${referral.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || '',
          },
          body: JSON.stringify({
            status: 'Pending',
            matched_providers: null,
            auto_matched_at: null,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || errorData.error || 'Failed to revert referral to Pending');
        }

        alert('Auto-match canceled. Referral reverted to Pending status.');
      } else {
        // Filter out the provider to remove
        const updatedProviders = referral.matched_providers!.filter(p => p.provider_id !== providerIdToRemove);

        // Update backend on Trust Marketplace API
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');

        const response = await fetch(`${API_BASE_URL}/api/referrals/${referral.id}/auto-match`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || '',
          },
          body: JSON.stringify({
            matched_providers: updatedProviders,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || errorData.error || 'Failed to remove provider');
        }
      }

      // Refresh data
      await fetchData();

    } catch (err) {
      console.error('Error removing provider:', err);
      alert('Failed to remove provider');
    } finally {
      setProcessingReferralId(null);
    }
  };

  if (loading) {
    return (
      <div className="container-page section-spacing">
        <LoadingSpinner size="lg" text="Loading referrals..." />
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={fetchData} />;
  }

  return (
    <div className="container-page section-spacing">
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} aria-label="Go back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div className="flex-1" />
        <Button variant="outline" icon={<RefreshCw className="w-4 h-4" />} onClick={fetchData}>
          Refresh
        </Button>
      </div>

      {/* Algorithm Status */}
      <Card className="mb-6 bg-white rounded-lg shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-teal-600" />
            Algorithm Status
          </CardTitle>
        </CardHeader>
        <CardContent>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-green-50 rounded-lg p-3 md:p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-600">Status</span>
              </div>
              <p className="text-xl md:text-2xl font-bold text-green-600 mt-2">{algorithmStatus.status}</p>
            </div>

            <div className="bg-blue-50 rounded-lg p-3 md:p-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                <span className="text-xs md:text-sm font-medium text-gray-600">Last Run</span>
              </div>
              <p className="text-xs md:text-sm font-semibold text-blue-600 mt-2">
                {fmt.dateTime(algorithmStatus.lastRun)}
              </p>
            </div>

            <div className="bg-teal-50 rounded-lg p-3 md:p-4">
              <div className="flex items-center space-x-2">
                <UserCheck className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                <span className="text-xs md:text-sm font-medium text-gray-600">Auto-Matched</span>
              </div>
              <p className="text-xl md:text-2xl font-bold text-teal-600 mt-2">{algorithmStatus.totalMatched}</p>
            </div>

            <div className="bg-orange-50 rounded-lg p-3 md:p-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                <span className="text-xs md:text-sm font-medium text-gray-600">Pending</span>
              </div>
              <p className="text-xl md:text-2xl font-bold text-orange-600 mt-2">{algorithmStatus.pendingMatches}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referrals List */}
      <div className="space-y-3 md:space-y-4">
        {referrals.length === 0 ? (
          <EmptyState
            icon={<UserCheck className="w-8 h-8 text-gray-400" />}
            title="No referrals pending or auto-matched"
            description="Create new referrals to see them here"
          />
        ) : (
            referrals.map((referral) => {
              const isExpanded = expandedReferralId === referral.id;
              const isProcessing = processingReferralId === referral.id;
              const statusLower = referral.status?.toLowerCase();
              const isPending = statusLower === 'pending';
              const isAutoMatched = statusLower === 'auto-matched';

              return (
                <div
                  key={referral.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                >
                  {/* Referral Header */}
                  <div className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                          <h3 className="text-base md:text-lg font-semibold text-gray-800 truncate">
                            {referral.first_name} {referral.last_name}
                          </h3>
                          <Badge variant={isPending ? 'warning' : 'success'} dot>
                            {referral.status}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3 text-xs md:text-sm">
                          <div className="flex items-center space-x-2 text-gray-600">
                            <span className="font-medium">Ref #:</span>
                            <span>{referral.referral_number}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{referral.zip_code}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-gray-600">
                            <span className="font-medium">Urgency:</span>
                            <span className={`font-semibold ${
                              referral.urgency_level === 'High' ? 'text-red-600' : 'text-orange-600'
                            }`}>
                              {referral.urgency_level}
                            </span>
                          </div>
                        </div>

                        <div className="mt-2 text-sm text-gray-600">
                          <span className="font-medium">Diagnosis:</span> {referral.primary_diagnosis}
                        </div>

                        {referral.sdoh_needs && referral.sdoh_needs.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {referral.sdoh_needs.map((need, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md"
                              >
                                {need}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 md:ml-4">
                        {isPending && (
                          <Button
                            variant="primary"
                            onClick={() => handleAutoMatch(referral)}
                            disabled={isProcessing}
                            loading={isProcessing}
                            icon={<UserCheck className="w-4 h-4" />}
                          >
                            {isProcessing ? 'Matching...' : 'Auto-Match'}
                          </Button>
                        )}

                        {isAutoMatched && referral.matched_providers && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleReferralExpansion(referral.id)}
                          >
                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Matched Providers (Expanded) */}
                  {isExpanded && isAutoMatched && referral.matched_providers && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4 md:p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 mb-4">
                        <h4 className="text-base md:text-md font-semibold text-gray-800">
                          Matched Providers ({referral.matched_providers.length})
                        </h4>

                        {referral.matched_providers.some(p => p.status === 'pending') && (
                          <Button
                            variant="primary"
                            onClick={() => handleSendToProviders(referral)}
                            disabled={isProcessing}
                            icon={<Send className="w-4 h-4" />}
                          >
                            Send to Providers
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:gap-4">
                        {referral.matched_providers.map((provider, idx) => (
                          <div
                            key={idx}
                            className="bg-white rounded-lg border border-gray-200 p-4"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h5 className="font-semibold text-gray-800">
                                  {provider.organization_name}
                                </h5>
                                <div className="flex items-center space-x-2 mt-1 text-sm text-gray-600">
                                  <MapPin className="w-3 h-3" />
                                  <span>{provider.distance_miles} miles away</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Badge
                                  variant={
                                    provider.status === 'accepted' ? 'success' :
                                    provider.status === 'sent' ? 'info' :
                                    provider.status === 'not_selected' ? 'default' :
                                    'warning'
                                  }
                                  size="sm"
                                >
                                  {provider.status}
                                </Badge>
                              </div>
                            </div>

                            {(() => {
                              const servicesList = parseServices(provider.services);
                              return servicesList.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {servicesList.slice(0, 3).map((service, sIdx) => (
                                    <span
                                      key={sIdx}
                                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                    >
                                      {service}
                                    </span>
                                  ))}
                                  {servicesList.length > 3 && (
                                    <span className="px-2 py-1 text-gray-500 text-xs">
                                      +{servicesList.length - 3} more
                                    </span>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Change/Remove Provider Buttons - only show if status is pending or sent */}
                            {(provider.status === 'pending' || provider.status === 'sent') && (
                              <div className="mt-3 flex items-center gap-2 pt-3 border-t border-gray-200">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleChangeProvider(referral, provider.provider_id)}
                                  disabled={isProcessing}
                                  icon={<Edit2 className="w-3 h-3" />}
                                >
                                  Change
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleRemoveProvider(referral, provider.provider_id)}
                                  disabled={isProcessing}
                                  icon={<X className="w-3 h-3" />}
                                >
                                  Remove
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

      {/* Provider Selection Modal */}
      <ProviderSelectionModal
        isOpen={showProviderModal}
        onClose={() => {
          setShowProviderModal(false);
          setEditingReferral(null);
          setProviderToReplace(null);
        }}
        onSelectProvider={handleProviderSelected}
        memberLocation={editingReferral?.latitude && editingReferral?.longitude ? {
          latitude: editingReferral.latitude,
          longitude: editingReferral.longitude
        } : undefined}
        currentProviders={editingReferral?.matched_providers?.map(p => p.provider_id) || []}
      />

      {/* Auto-Match Confirmation Dialog */}
      {showConfirmationDialog && confirmingReferral && (
        <AutoMatchConfirmationDialog
          referral={{
            id: confirmingReferral.id,
            first_name: confirmingReferral.first_name,
            last_name: confirmingReferral.last_name,
            sdoh_needs: confirmingReferral.sdoh_needs,
            zip_code: confirmingReferral.zip_code
          }}
          matchedProviders={(confirmingReferral.matched_providers || []).map(mp => {
            // Enrich matched provider with contact details from providers list
            const fullProvider = providers.find(p => p.id === mp.provider_id);
            return {
              ...mp,
              phone: fullProvider?.primary_phone || fullProvider?.phone_number || fullProvider?.phone,
              email: fullProvider?.primary_email || fullProvider?.email,
              website: fullProvider?.website,
              address: fullProvider?.street_address || fullProvider?.address
            };
          })}
          onConfirm={handleConfirmSend}
          onCancel={() => {
            setShowConfirmationDialog(false);
            setConfirmingReferral(null);
          }}
        />
      )}
    </div>
  );
}
